import Spinner from "@/components/common/Spinner";
import { buttonVariants } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { randomHappyEmoji, wordsOfPraise } from "@/utils/positivePatientWords";
import { Link, createFileRoute } from "@tanstack/react-router";
import { ArrowRight } from "lucide-react";
import type React from "react";
import { useEffect, useState } from "react";

const EntryComplete = () => {
	const [isMinLoading, setIsMinLoading] = useState(true);
	const [emoji] = useState(() => randomHappyEmoji());
	const [praise] = useState(() => wordsOfPraise());

	useEffect(() => {
		const timer = setTimeout(() => {
			setIsMinLoading(false);
		}, 1000);

		return () => clearTimeout(timer);
	}, []);

	return (
		<section className="flex flex-col gap-y-6 md-width">
			{isMinLoading ? (
				<div className="space-y-6 text-center">
					<div className="space-y-2 text-center">
						<p className="bg-clip-text bg-gradient-to-r from-sky-600 to-pink-600 font-semibold text-transparent text-2xl">
							Almost there! <span className="text-slate-900">{emoji}</span>
						</p>
						<p className="text-lg">{praise}</p>
					</div>

					<GradientBox>
						<Spinner />
					</GradientBox>
				</div>
			) : (
				<div className="space-y-6 text-center">
					<div className="space-y-2 text-center">
						<p className="bg-clip-text bg-gradient-to-r from-sky-600 to-pink-600 font-semibold text-transparent text-2xl">
							Congratulations! <span className="text-slate-900">{emoji}</span>
						</p>
						<p className="text-lg">You've successfully completed this form!</p>
					</div>

					<GradientBox>
						<p className="font-medium text-lg">
							Thank you for your submission!
						</p>
						<p className="text-muted-foreground">
							Your information has been recorded successfully.
						</p>
					</GradientBox>
				</div>
			)}

			{!isMinLoading && (
				<Link
					data-testid="form-complete-button"
					className={cn(
						buttonVariants({ size: "lg" }),
						"mx-auto mt-4 group flex items-center gap-2 bg-gradient-to-r from-indigo-500/90 to-indigo-600/90 hover:from-indigo-600/90 hover:to-indigo-700/90 text-white shadow-md transition-all duration-300",
					)}
					to="/"
				>
					Return to Home
					<ArrowRight className="ml-1 w-4 h-4 transition-transform group-hover:translate-x-1" />
				</Link>
			)}
		</section>
	);
};

export const Route = createFileRoute("/public/form/$formId/complete")({
	component: EntryComplete,
});

const GradientBox = ({ children }: { children: React.ReactNode }) => {
	return (
		<div className="space-y-3 bg-gradient-to-br from-pink-200/30 to-indigo-200/40 shadow-sm mx-auto p-6 border border-slate-200 rounded-lg w-fit min-w-60 min-h-60 transition-all duration-500">
			{children}
		</div>
	);
};
